import type { MouseEvent } from "react";

let activeScrollFrame: number | null = null;

function prefersReducedMotion() {
  return (
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

function easeInOutCubic(t: number) {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

function animateScrollTo(top: number) {
  if (activeScrollFrame !== null) {
    window.cancelAnimationFrame(activeScrollFrame);
  }

  const start = window.scrollY;
  const distance = top - start;
  const duration = Math.min(900, Math.max(420, Math.abs(distance) * 0.35));
  const startedAt = window.performance.now();

  const tick = (now: number) => {
    const elapsed = now - startedAt;
    const progress = Math.min(1, elapsed / duration);
    const eased = easeInOutCubic(progress);

    window.scrollTo(0, start + distance * eased);

    if (progress < 1) {
      activeScrollFrame = window.requestAnimationFrame(tick);
    } else {
      activeScrollFrame = null;
      window.scrollTo(0, top);
    }
  };

  activeScrollFrame = window.requestAnimationFrame(tick);
}

export function scrollToLandingTarget(hash: string) {
  if (typeof window === "undefined") return false;

  const targetId = hash.replace(/^#/, "");
  const target = targetId ? document.getElementById(targetId) : document.body;
  if (!target) return false;

  const headerHeight =
    document.querySelector("header")?.getBoundingClientRect().height ?? 0;
  const offset = targetId === "hero" ? 0 : headerHeight + 12;
  const top = Math.max(
    0,
    target.getBoundingClientRect().top + window.scrollY - offset,
  );

  if (window.location.hash !== hash) {
    window.history.pushState(null, "", hash);
  }

  if (prefersReducedMotion()) {
    window.scrollTo({ top, behavior: "auto" });
  } else {
    animateScrollTo(top);
  }

  return true;
}

export function handleLandingAnchorClick(
  event: MouseEvent<HTMLAnchorElement>,
  href: string,
) {
  if (!href.startsWith("#")) return false;

  const didScroll = scrollToLandingTarget(href);
  if (didScroll) {
    event.preventDefault();
  }

  return didScroll;
}
